/**
 * スクロールで要素をアニメーション表示
 *
 * 使用方法:
 * - クラス: .js-scroll-reveal (後方互換性のため)
 * - 属性: data-scroll-animation="fade-in" (推奨)
 *
 * アニメーションタイプ:
 * - fade-in: フェードイン (デフォルト)
 * - fade-in-up: 下からフェードイン
 * - fade-in-down: 上からフェードイン
 * - fade-in-left: 左からフェードイン
 * - fade-in-right: 右からフェードイン
 * - scale-in: スケールイン
 * - clip-up: 下から表示 (clip-path)
 * - clip-down: 上から表示 (clip-path)
 * - wipe-right: 左から右へ表示 (clip-path)
 * - wipe-left: 右から左へ表示 (clip-path)
 *
 * オプション属性:
 * - data-scroll-offset: 発火位置（ビューポート下端からのpx距離、デフォルト: 100）
 *   値が大きいほど早く発火する（例: "150" → 画面下端から150px手前で発火）
 * - data-scroll-delay: アニメーション遅延 (例: "0.2s")
 * - data-scroll-duration: アニメーション時間 (例: "0.8s")
 *
 * 前提条件:
 * - source/js/main.js に以下を追加:
 *   import { scrollReveal } from './modules/scroll-reveal.js';
 *   scrollReveal(); // DOMContentLoaded内で呼び出す
 */
export function scrollReveal({ startDelay = 0 } = {}) {
  const elements = document.querySelectorAll(
    '[data-scroll-animation], .js-scroll-reveal',
  );
  if (!elements.length) return;

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  if (prefersReducedMotion) {
    elements.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  // data-scroll-duration をインラインスタイルへ反映
  elements.forEach((el) => {
    if (el.dataset.scrollDuration) {
      el.style.transitionDuration = el.dataset.scrollDuration;
    }
  });

  // 同じoffset値の要素は同じobserverを共有
  const observerMap = new Map();

  const observe = () => {
    elements.forEach((el) => {
      const offset = parseInt(el.dataset.scrollOffset) || 100;

      if (!observerMap.has(offset)) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                // data-scroll-delay を setTimeout で実現することで
                // ページロード時・スクロール時ともに確実に時間差アニメーションが効く
                const delayMs = entry.target.dataset.scrollDelay
                  ? parseFloat(entry.target.dataset.scrollDelay) * 1000
                  : 0;
                setTimeout(
                  () => entry.target.classList.add('is-visible'),
                  delayMs,
                );
                observer.unobserve(entry.target);
              }
            });
          },
          {
            threshold: 0,
            rootMargin: `0px 0px -${offset}px 0px`,
          },
        );
        observerMap.set(offset, observer);
      }

      observerMap.get(offset).observe(el);
    });
  };

  // ローディングがある場合は退場後に、ない場合は描画後すぐに observe 開始
  setTimeout(
    () => requestAnimationFrame(() => requestAnimationFrame(observe)),
    startDelay,
  );
}
